import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Tag, Plus, Edit, Trash2, BarChart3, Search, RefreshCw, X, IndianRupee, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';

const PRODUCT_TYPE_OPTIONS = [
  { value: 'meeting_room', label: 'Meeting Room' },
  { value: 'conference_room', label: 'Conference Room' },
  { value: 'event_space', label: 'Event Space' },
  { value: 'day_pass', label: 'Day Pass' },
  { value: 'virtual_office', label: 'Virtual Office' },
  { value: 'dedicated_desk', label: 'Dedicated Desk' },
  { value: 'training_room', label: 'Training Room' },
  { value: 'podcast_room', label: 'Podcast Room' },
];

const getErrorMessage = (e, fallback) => {
  const detail = e.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (d?.loc ? `${d.loc[d.loc.length - 1]}: ${d.msg}` : d?.msg))
      .filter(Boolean)
      .join('; ') || fallback;
  }
  return fallback;
};

const emptyForm = () => ({
  code: '',
  discount_percentage: 10,
  applicable_product_types: [],
  applicable_center_ids: [],
  valid_till: '',
  status: 'active',
  usage_limit: '',
  per_user_limit: 3,
  description: '',
});

export const CouponsTab = ({ api, venues = [] }) => {
  const [list, setList] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [usageOpen, setUsageOpen] = useState(null);
  const [usageData, setUsageData] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const [items, summary] = await Promise.all([
        api.get('/coupons', { params }).catch(() => ({ data: [] })),
        api.get('/coupons/analytics/summary').catch(() => ({ data: null })),
      ]);
      setList(items.data);
      setAnalytics(summary.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter]);

  const filtered = useMemo(() => {
    if (!search) return list;
    return list.filter(c => (c.code || '').toLowerCase().includes(search.toLowerCase()));
  }, [list, search]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      code: c.code,
      discount_percentage: c.discount_percentage,
      applicable_product_types: c.applicable_product_types || [],
      applicable_center_ids: c.applicable_center_ids || [],
      valid_till: c.valid_till ? c.valid_till.substring(0, 10) : '',
      status: c.status,
      usage_limit: c.usage_limit || '',
      per_user_limit: c.per_user_limit ?? 3,
      description: c.description || '',
    });
    setShowForm(true);
  };

  const save = async () => {
    const code = (form.code || '').toUpperCase().replace(/\s/g, '');
    if (!/^[A-Z0-9]{3,30}$/.test(code)) { toast.error('Code must be 3-30 alphanumeric chars'); return; }
    if (!form.valid_till) { toast.error('Pick a valid-till date'); return; }
    if (!form.discount_percentage || form.discount_percentage <= 0) { toast.error('Discount % must be > 0'); return; }
    setSaving(true);
    try {
      const payload = {
        code,
        discount_percentage: Number(form.discount_percentage),
        applicable_product_types: form.applicable_product_types,
        applicable_center_ids: form.applicable_center_ids,
        valid_till: form.valid_till,
        status: form.status,
        usage_limit: form.usage_limit ? Number(form.usage_limit) : null,
        per_user_limit: Number(form.per_user_limit),
        description: form.description || null,
      };
      if (editing) {
        const { code: _ignored, ...editable } = payload;
        await api.patch(`/coupons/${editing.id}`, editable);
        toast.success('Coupon updated');
      } else {
        await api.post('/coupons', payload);
        toast.success('Coupon created');
      }
      setShowForm(false);
      fetchAll();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to save coupon'));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/coupons/${c.id}`, { status: c.status === 'active' ? 'inactive' : 'active' });
      fetchAll();
    } catch (e) {
      toast.error('Failed to toggle status');
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    try {
      const res = await api.delete(`/coupons/${c.id}`);
      toast.success(res.data?.message || 'Deleted');
      fetchAll();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to delete'));
    }
  };

  const openUsage = async (c) => {
    setUsageOpen(c);
    try {
      const res = await api.get(`/coupons/${c.id}/usage`);
      setUsageData(res.data);
    } catch (e) {
      toast.error('Failed to load usage');
    }
  };

  const toggleProductType = (val) => {
    setForm(f => ({
      ...f,
      applicable_product_types: f.applicable_product_types.includes(val)
        ? f.applicable_product_types.filter(v => v !== val)
        : [...f.applicable_product_types, val],
    }));
  };

  const toggleCenter = (val) => {
    setForm(f => ({
      ...f,
      applicable_center_ids: f.applicable_center_ids.includes(val)
        ? f.applicable_center_ids.filter(v => v !== val)
        : [...f.applicable_center_ids, val],
    }));
  };

  return (
    <div className="space-y-6" data-testid="coupons-tab">
      {/* KPI strip */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Active" value={analytics.counts.active} icon={<Tag className="w-4 h-4 text-emerald-500" />} />
          <KpiCard label="Expired" value={analytics.counts.expired} icon={<Tag className="w-4 h-4 text-red-500" />} />
          <KpiCard label="Redemptions" value={analytics.redemptions.total} icon={<TrendingUp className="w-4 h-4 text-blue-500" />} />
          <KpiCard label="Discount Given" value={`₹${analytics.redemptions.total_discount_given.toLocaleString('en-IN')}`} icon={<IndianRupee className="w-4 h-4 text-amber-500" />} />
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading flex items-center gap-2"><Tag className="w-5 h-5 text-primary" /> Coupon Management</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Create, monitor and manage discount coupons across all external bookings.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchAll} data-testid="coupons-refresh-btn"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
            <Button size="sm" onClick={openCreate} data-testid="coupons-new-btn"><Plus className="w-4 h-4 mr-1" /> New Coupon</Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search by code..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="coupons-search" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="sm:w-48" data-testid="coupons-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <p className="text-center text-sm text-muted-foreground py-8">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">No coupons yet. Click "New Coupon" to create one.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="text-left text-xs uppercase text-muted-foreground">
                    <th className="px-3 py-2">Code</th>
                    <th className="px-3 py-2">Discount %</th>
                    <th className="px-3 py-2">Products</th>
                    <th className="px-3 py-2">Centers</th>
                    <th className="px-3 py-2">Valid Till</th>
                    <th className="px-3 py-2">Usage</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Created</th>
                    <th className="px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(c => {
                    const products = (c.applicable_product_types || []);
                    const centers = (c.applicable_center_ids || []);
                    return (
                      <tr key={c.id} className="border-t hover:bg-muted/20" data-testid={`coupon-row-${c.code}`}>
                        <td className="px-3 py-2 font-mono font-bold">{c.code}</td>
                        <td className="px-3 py-2">{c.discount_percentage}%</td>
                        <td className="px-3 py-2 text-xs">{products.length === 0 ? 'All products' : products.map(p => p.replace(/_/g, ' ')).join(', ')}</td>
                        <td className="px-3 py-2 text-xs">{centers.length === 0 ? 'All centers' : `${centers.length} center(s)`}</td>
                        <td className="px-3 py-2 text-xs">{c.valid_till ? new Date(c.valid_till).toLocaleDateString() : '—'}</td>
                        <td className="px-3 py-2 text-xs">{c.usage_count || 0}{c.usage_limit ? ` / ${c.usage_limit}` : ''}</td>
                        <td className="px-3 py-2">
                          {c.is_expired ? <Badge variant="secondary" className="text-xs bg-red-500/15 text-red-700">Expired</Badge> :
                            c.status === 'active' ? <Badge className="text-xs bg-emerald-500/15 text-emerald-700">Active</Badge> :
                              <Badge variant="secondary" className="text-xs">Inactive</Badge>}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{c.created_by_name || '—'}<br />{new Date(c.created_at).toLocaleDateString()}</td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openUsage(c)} title="Usage" data-testid={`coupon-usage-${c.code}`}><BarChart3 className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => openEdit(c)} title="Edit" data-testid={`coupon-edit-${c.code}`}><Edit className="w-3.5 h-3.5" /></Button>
                            <Switch checked={c.status === 'active'} onCheckedChange={() => toggleActive(c)} data-testid={`coupon-toggle-${c.code}`} />
                            <Button variant="ghost" size="sm" onClick={() => remove(c)} title="Delete" className="text-red-500" data-testid={`coupon-delete-${c.code}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="coupons-form-modal">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.code}` : 'New Coupon'}</DialogTitle>
            <DialogDescription>{editing ? 'Update the coupon settings. The code itself cannot be changed.' : 'Define the coupon code, discount, applicability and validity.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Coupon Code *</Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s/g, '') })}
                  placeholder="CLAY50"
                  disabled={!!editing}
                  data-testid="coupon-form-code"
                />
              </div>
              <div>
                <Label>Discount % *</Label>
                <Input type="number" min="1" max="80" value={form.discount_percentage} onChange={(e) => setForm({ ...form, discount_percentage: e.target.value })} data-testid="coupon-form-pct" />
              </div>
            </div>
            <div>
              <Label>Applicable Products (leave empty for all)</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-1">
                {PRODUCT_TYPE_OPTIONS.map(opt => (
                  <label key={opt.value} className={`flex items-center gap-1.5 text-xs p-2 rounded border cursor-pointer ${form.applicable_product_types.includes(opt.value) ? 'border-primary bg-primary/10' : 'border-border'}`}>
                    <input type="checkbox" checked={form.applicable_product_types.includes(opt.value)} onChange={() => toggleProductType(opt.value)} className="accent-primary" />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Applicable Centers (leave empty for all)</Label>
              <div className="max-h-40 overflow-y-auto grid grid-cols-2 gap-1.5 mt-1 p-2 border rounded">
                {venues.map(v => (
                  <label key={v.id} className={`flex items-center gap-1.5 text-xs p-1.5 rounded border cursor-pointer ${form.applicable_center_ids.includes(v.id) ? 'border-primary bg-primary/10' : 'border-border'}`}>
                    <input type="checkbox" checked={form.applicable_center_ids.includes(v.id)} onChange={() => toggleCenter(v.id)} className="accent-primary" />
                    <span className="truncate">{v.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Valid Till *</Label>
                <Input type="date" value={form.valid_till} onChange={(e) => setForm({ ...form, valid_till: e.target.value })} data-testid="coupon-form-validtill" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Total Usage Limit (optional)</Label>
                <Input type="number" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} placeholder="Leave empty for unlimited" />
              </div>
              <div>
                <Label>Per-Customer Limit</Label>
                <Input type="number" value={form.per_user_limit} onChange={(e) => setForm({ ...form, per_user_limit: e.target.value })} placeholder="3" />
              </div>
            </div>
            <div>
              <Label>Description (shown to customer on apply)</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Launch offer, corporate tie-up, etc." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="coupon-form-save">{saving ? 'Saving…' : (editing ? 'Update' : 'Create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Usage modal */}
      <Dialog open={!!usageOpen} onOpenChange={(o) => { if (!o) { setUsageOpen(null); setUsageData(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="coupon-usage-modal">
          <DialogHeader>
            <DialogTitle className="font-mono">{usageOpen?.code} — Usage</DialogTitle>
            <DialogDescription>{usageData ? `${usageData.total_redemptions} redemption(s) · ₹${usageData.total_discount_given.toLocaleString('en-IN')} discount given` : 'Loading…'}</DialogDescription>
          </DialogHeader>
          {usageData && (
            <div className="overflow-x-auto rounded-lg border mt-2">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                  <tr><th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-left">Customer</th><th className="px-3 py-2 text-right">Original</th><th className="px-3 py-2 text-right">Discount</th><th className="px-3 py-2 text-right">Final</th></tr>
                </thead>
                <tbody>
                  {usageData.redemptions.map(r => (
                    <tr key={r.id} className="border-t">
                      <td className="px-3 py-2 text-xs">{new Date(r.redeemed_at).toLocaleString()}</td>
                      <td className="px-3 py-2 text-xs">{r.guest_email}</td>
                      <td className="px-3 py-2 text-right text-xs">₹{r.original_amount.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2 text-right text-xs text-emerald-600">- ₹{r.discount_amount.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2 text-right text-xs font-semibold">₹{r.final_amount.toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                  {usageData.redemptions.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-6 text-muted-foreground text-xs">No redemptions yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const KpiCard = ({ label, value, icon }) => (
  <Card>
    <CardContent className="p-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">{icon}{label}</div>
      <p className="text-2xl font-heading font-bold mt-1">{value}</p>
    </CardContent>
  </Card>
);

export default CouponsTab;
